import '@/i18n/utils/datepicker'

import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'

import ScrollToTop from '@/components/atoms/AmScrollToTop'
import AmPrivateRoute from '@/components/atoms/Routing/AmPrivateRoute'
import { scheduleTemplateVariant } from '@/constants'
import { CreateScheduleProvider } from '@/contexts/createSchedule'
import { userAuthenticated } from '@/redux/auth/selectors'
import { messageHandlerReset } from '@/redux/messageHandler/actions'
import { messageHandlerFullInfo } from '@/redux/messageHandler/selectors'
import { getUserDataRequest } from '@/redux/user/actions'

const ApproveDoctor = React.lazy(() => import('@/pages/ApproveDoctor'))
const CreateSchedule = React.lazy(() => import('@/pages/CreateSchedule'))
const CreateScheduleTemplate = React.lazy(() => import('@/pages/CreateScheduleTemplate'))
const Dashboard = React.lazy(() => import('@/pages/Dashboard'))
const DeleteAccount = React.lazy(() => import('@/pages/DeleteAccount'))
const EditSchedule = React.lazy(() => import('@/pages/EditSchedule'))
const EditScheduleTemplate = React.lazy(() => import('@/pages/EditScheduleTemplate'))
const Login = React.lazy(() => import('@/pages/Login'))
const LoginWithTokenConfirmation = React.lazy(() => import('@/pages/LoginWithTokenConfirmation'))
const Patient = React.lazy(() => import('@/pages/Patient'))
const Patients = React.lazy(() => import('@/pages/Patients'))
const Profile = React.lazy(() => import('@/pages/Profile'))
const Schedule = React.lazy(() => import('@/pages/Schedule'))
const ScheduleTemplate = React.lazy(() => import('@/pages/ScheduleTemplate'))
const ScheduleTemplates = React.lazy(() => import('@/pages/ScheduleTemplates'))
const Styleguide = React.lazy(() => import('@/pages/Styleguide'))
const UniversalLink = React.lazy(() => import('@/pages/UniversalLink'))
const PaymentSuccess = React.lazy(() => import('@/pages/PaymentSuccess'))
const BlockedSuccess = React.lazy(() => import('@/pages/BlockedSuccess'))

const About = React.lazy(() => import('@/pages/About'))
const OrganizationInvite = React.lazy(() => import('@/pages/OrganizationInvite'))

function App () {
  const hasGeneralMessage = useSelector(messageHandlerFullInfo)
  const isAuthenticated = useSelector(userAuthenticated)
  const dispatch = useDispatch()
  const toast = useToast()
  const { i18n: { dir, language } } = useTranslation()

  useEffect(() => {
    document.dir = dir()
  }, [language])

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUserDataRequest())
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (hasGeneralMessage?.description) {
      toast({
        title: hasGeneralMessage?.message,
        description: hasGeneralMessage.description,
        status: hasGeneralMessage?.type,
        duration: hasGeneralMessage?.notDismissable ? null : (hasGeneralMessage?.duration || 3000),
        isClosable: true,
        position: 'top',
        onCloseComplete: () => {
          dispatch(messageHandlerReset())
        },
        containerStyle: {
          whiteSpace: 'break-spaces'
        }
      })
    }
  }, [hasGeneralMessage, toast, dispatch])

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route
          path='/'
          element={
            <AmPrivateRoute enabledRoles={['doctor', 'patient']}>
              <Dashboard />
            </AmPrivateRoute>
          }
        />
        <Route
          path='/patients/:patientId'
          element={
            <AmPrivateRoute enabledRoles={['doctor']}>
              <Patient />
            </AmPrivateRoute>
          }
        />
        <Route
          path='/patients'
          element={
            <AmPrivateRoute enabledRoles={['doctor']}>
              <Patients />
            </AmPrivateRoute>
          }
        />
        <Route
          path='/schedule-template/:scheduleTemplateId'
          element={
            <AmPrivateRoute enabledRoles={['doctor']}>
              <ScheduleTemplate />
            </AmPrivateRoute>
          }
        />
        <Route
          path='/schedule-templates'
          element={
            <AmPrivateRoute enabledRoles={['doctor']}>
              <ScheduleTemplates />
            </AmPrivateRoute>
          }
        />
        <Route
          path='/create-schedule-template'
          element={
            <AmPrivateRoute enabledRoles={['doctor']}>
              <CreateScheduleProvider variant={scheduleTemplateVariant}>
                <CreateScheduleTemplate />
              </CreateScheduleProvider>
            </AmPrivateRoute>
          }
        />
        <Route
          path='/edit-schedule-template/:templateId'
          element={
            <AmPrivateRoute enabledRoles={['doctor']}>
              <EditScheduleTemplate />
            </AmPrivateRoute>
          }
        />
        <Route
          path='/profile'
          element={
            <AmPrivateRoute enabledRoles={['doctor', 'patient']}>
              <Profile />
            </AmPrivateRoute>
          }
        />
        <Route
          path='/about'
          element={
            <AmPrivateRoute enabledRoles={['doctor', 'patient']}>
              <About />
            </AmPrivateRoute>
          }
        />
        <Route
          path='/schedules/:scheduleId'
          element={
            <AmPrivateRoute enabledRoles={['doctor', 'patient']}>
              <Schedule />
            </AmPrivateRoute>
          }
        />
        <Route
          path='/create-schedule'
          element={
            <AmPrivateRoute enabledRoles={['doctor']}>
              <CreateScheduleProvider>
                <CreateSchedule />
              </CreateScheduleProvider>
            </AmPrivateRoute>
          }
        />
        <Route
          path='/edit-schedule/:scheduleId'
          element={
            <AmPrivateRoute enabledRoles={['doctor']}>
              <EditSchedule />
            </AmPrivateRoute>
          }
        />
        <Route path='/universal/*' element={<UniversalLink />} />
        <Route path='/delete-account-info' element={<DeleteAccount />} />
        <Route path='/payment/success' element={<PaymentSuccess />} />
        <Route path='/blocked/success' element={<BlockedSuccess />} />
        <Route path='/partner/invite/:token' element={<OrganizationInvite />} />
        <Route path='/invitation/:token' element={<ApproveDoctor />} />
        <Route
          path='/token-login/:token'
          element={<LoginWithTokenConfirmation />}
        />
        <Route path='/login' element={<Login />} />
        <Route path='/styleguide' element={<Styleguide />} />
        <Route path='*' element={<Navigate to='/' />} />
      </Routes>
    </>
  )
}

export default App
